import { useSyncExternalStore } from "react";

/* ══════════════════════════════════════════════════════════════════
   Woran die Anwendung merkt, worauf sie läuft.

   Luca, 17.09.2026: *„Es muss automatisch erkannt werden, dass ich auf
   dem Mobile bin — das geht ja jetzt theoretisch nur über Handy als
   URL, das macht keinen Sinn. Im PWA-Konstrukt muss das die Dinge
   selbstständig erkennen können."* Und dazu: *„Das muss ja keine extra
   URL sein, und die Module müssen alle mobile-fähig sein."*

   ── Warum das eine eigene Datei ist ───────────────────────────────

   Bis hierher stand die Frage an **einer** Stelle als eine Zeile im
   Routenbaum: `window.matchMedia("(max-width: 640px)")`, gelesen beim
   Wegwechsel. Das hatte drei Löcher, und alle drei hat Luca gefunden:

   1. **Der Weg entschied mit.** Wer `/handy…` öffnete, bekam die
      Telefonfassung, wer `/auftraege` öffnete, die Schreibtischmaske —
      auf demselben Gerät. Die Adresse ist aber keine Auskunft über das
      Gerät, sondern über das Ziel.
   2. **Nur die Breite zählte.** Ein Telefon im Querformat ist 844 px
      breit und damit nach dieser Rechnung ein Schreibtisch.
   3. **Gefragt wurde nur beim Wegwechsel.** Wer drehte, blieb in der
      falschen Fassung stehen, bis er irgendwohin navigierte.

   Diese Datei beantwortet die Frage an **einer** Stelle für die ganze
   Anwendung — für den Wegwächter, für die Masken und für die Module,
   die parallel telefontauglich gebaut werden. Zwei Antworten auf
   dieselbe Frage wären zwei Oberflächen.

   ── Die Merkmale ──────────────────────────────────────────────────

   | Merkmal | was es sagt |
   |---|---|
   | `max-width: 640px` | schmal — dieselbe Grenze wie in `stile/handy.css` |
   | `max-height: 540px` | flach — das gedrehte Telefon |
   | `pointer: coarse` | bedient wird mit dem Finger, nicht mit der Maus |

   **Gefragt wird das Fenster, nicht das Gerät** — dasselbe, was Material
   („window size classes"), Apple (Size Classes) und Windows tun. Kein
   User-Agent, keine Adresse, kein „ist installiert". Ein Telefon im
   Browser und dasselbe Telefon als installierte App sind **dieselbe** Lage.

   Daraus **drei Lagen** und nicht zwei: Ein Tablet ist kein Telefon
   (der Daumen erreicht nicht beide Ränder) und kein Schreibtisch (es
   gibt keinen Mauszeiger, der ein 24-px-Ziel trifft). Wer nur wissen
   will, ob die Telefonfassung gilt, fragt `useTelefon()`; wer den
   Unterschied braucht, `useGeraet()`.

   ── Was wann ein Telefon ist ──────────────────────────────────────

   1. **Der Schirm ist schmal** (≤ 640 px). Das alte Maß, unverändert.
   2. **Finger und flach** (≤ 540 px hoch): das gedrehte Telefon. Die
      Höhe ist hier die ehrlichere Zahl — 844 × 390 ist dasselbe Gerät
      wie 390 × 844, und nur die kurze Kante sagt das.

   ── „Installiert" entscheidet nicht mehr mit — 19.09.2026 ─────────

   Bis hierher gab es eine dritte Regel: **installiert, mit Finger, bis
   820 px breit → Telefon.** Sie war die einzige, die ein Merkmal des
   Geräts statt des Fensters fragte, und sie hatte eine Folge, die niemand
   wollte: Ein iPad Air hochkant (820 px) war im Browser ein Tablet mit
   Seitenleiste und als installierte App ein Telefon mit Leiste unten —
   dasselbe Gerät, dieselbe Hand, zwei Oberflächen. Ein iPad Pro (834 px)
   blieb in beiden Fällen Tablet. 40-mobiles-grundgeruest.md hatte das
   Herausnehmen empfohlen (die drei Designsysteme raten davon ab); Luca,
   19.09.2026: *„Komplett einmal hinterfragen, wie die PWA aufgebaut ist,
   sodass es mobile sauber erkannt wird … State of the Art Mobile
   Responsive Design."* Das ist es: das Fenster entscheidet.

   **Kein Telefon verliert dabei etwas.** Jedes Telefon ist hochkant
   höchstens 430 px breit (Regel 1) und quer höchstens 440 px hoch
   (Regel 2). Was zwischen 641 und 820 px liegt und mit dem Finger bedient
   wird, ist ein kleines Tablet oder ein aufgeklapptes Faltgerät — und das
   ist nach Material und Apple ein Tablet.

   `istInstalliert()` bleibt trotzdem: für den Installhinweis und das
   Herunterziehen zum Aktualisieren. Mit der Lage hat es nichts zu tun.

   **Ohne `matchMedia` wird nicht geraten.** Im Test und serverseitig
   gibt es keine Medienabfrage; dann entscheidet die Fensterbreite, und
   gibt es auch die nicht, ist es ein Schreibtisch. Eine Anwendung, die
   ohne Auskunft die Telefonfassung wählt, zeigt im Zweifel die kleinere
   Maske — und die kleinere ist die, der etwas fehlt.
   ══════════════════════════════════════════════════════════════════ */

export type Geraet = "telefon" | "tablet" | "schreibtisch";

/** Dieselbe Grenze wie ganz unten in `stile/handy.css`. */
const SCHMAL = "(max-width: 640px)";
/** Das gedrehte Telefon: breit genug, aber viel zu flach. */
const FLACH = "(max-height: 540px)";
/** Bedient wird mit dem Finger — kein Zeiger, der 24 px trifft. */
const GROB = "(pointer: coarse)";
/** Ein Tablet ist bis hierher breit; darüber ist es ein Schirm. */
const MITTEL = "(max-width: 1024px)";
/** Installiert — ohne Browserrahmen gestartet. Keine Frage der Lage (oben). */
const INSTALLIERT =
  "(display-mode: standalone), (display-mode: minimal-ui), (display-mode: fullscreen)";

/** Die Abfragen, aus denen die Lage folgt — einmal angelegt, danach nur gelesen. */
const ABFRAGEN = [SCHMAL, FLACH, GROB, MITTEL] as const;

const gemerkt = new Map<string, MediaQueryList>();

/** Die Medienabfrage — oder `null`, wo es keine gibt (Test, Server). */
function abfrage(text: string): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
  let mql = gemerkt.get(text);
  if (!mql) {
    mql = window.matchMedia(text);
    gemerkt.set(text, mql);
  }
  return mql;
}

function passt(text: string): boolean {
  return abfrage(text)?.matches ?? false;
}

/**
 * Läuft die Anwendung installiert, ohne Browserrahmen?
 *
 * Safari auf dem iPhone kennt `display-mode` erst spät und führt daneben
 * sein eigenes `navigator.standalone` — es wird mitgelesen, weil genau
 * dieses Gerät der Regelfall auf dem Hof ist.
 */
export function istInstalliert(): boolean {
  if (passt(INSTALLIERT)) return true;
  if (typeof navigator === "undefined") return false;
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** Was die Abfragen gerade sagen — ohne die Ruhe beim Tippen (unten). */
function berechnen(): Geraet {
  // Keine Medienabfrage: Dann sagt die Fensterbreite, was sie sagen kann.
  if (abfrage(SCHMAL) === null) {
    const breit = typeof window === "undefined" ? 0 : window.innerWidth;
    return breit > 0 && breit <= 640 ? "telefon" : "schreibtisch";
  }

  const grob = passt(GROB);
  if (passt(SCHMAL)) return "telefon";
  if (grob && passt(FLACH)) return "telefon";
  if (grob && passt(MITTEL)) return "tablet";
  return "schreibtisch";
}

/** Gilt die Telefonfassung? Die eine Frage, die die meisten Masken haben. */
export function istTelefon(): boolean {
  return geraetJetzt() === "telefon";
}

/* ══════════════ Wer erfährt, dass sich die Lage geändert hat ══════════════

   Gedreht, geteilte Ansicht, Fenster gezogen, Tastatur auf: All das
   ändert die Antwort, und beides muss folgen — der Wegwächter und die
   Maske. Gemeldet wird deshalb nur, wenn sich die **Lage** ändert und
   nicht bei jedem Pixel: Ein `router.invalidate()` an jedem Bild einer
   Ziehbewegung wäre ein Schirm, der flackert, statt zu folgen. */

const horcher = new Set<() => void>();
let lage: Geraet = "schreibtisch";
let breite = 0;
let haengt = false;

/* ── Die Tastatur ist kein Lagewechsel (17.09.2026) ──
   Seit `interactive-widget=resizes-content` (index.html) macht Chrome auf
   Android das Layout kleiner, wenn die Tastatur aufgeht. Ein Android-Tablet
   quer ist dann plötzlich 1280 × 450 hoch — nach der Regel „Finger und
   flach" ein Telefon: Leiste unten statt Seitenleiste, mitten im Tippen.
   **Ändert sich nur die Höhe, während jemand in einem Feld steht, bleibt
   die Lage.** Dreht er das Gerät, ändert sich die Breite, und es gilt die
   neue; verlässt er das Feld, wird nachgesehen. */
function tippt(): boolean {
  if (typeof document === "undefined") return false;
  const el = document.activeElement;
  return el instanceof HTMLElement
    && (el.isContentEditable || el.matches("input, textarea, select"));
}

function nachsehen(): void {
  const neu = berechnen();
  const jetztBreit = typeof window === "undefined" ? 0 : window.innerWidth;
  if (neu === lage) {
    breite = jetztBreit;
    return;
  }
  if (jetztBreit === breite && tippt()) return;
  lage = neu;
  breite = jetztBreit;
  for (const h of horcher) h();
}

/**
 * Was gerade gilt — ohne React, für den Wegwächter im Routenbaum.
 *
 * Sobald die Abfragen gehört werden, ist es die gemeldete Lage und nicht
 * eine neue Rechnung: Sonst sähe der Wächter beim Tippen ein Telefon, das
 * die Stile und `useGeraet()` nicht sehen.
 */
export function geraetJetzt(): Geraet {
  return haengt ? lage : berechnen();
}

function einhaengen(): void {
  if (haengt || typeof window === "undefined") return;
  haengt = true;
  lage = berechnen();
  breite = window.innerWidth;
  for (const text of ABFRAGEN) abfrage(text)?.addEventListener("change", nachsehen);
  /* `resize` zusätzlich: Ohne `matchMedia` ist es die einzige Meldung,
     und mit ihr fängt es die Fälle, in denen ein Browser eine Abfrage
     nicht neu auswertet (geteilte Ansicht auf dem Tablet). */
  window.addEventListener("resize", nachsehen);
  window.addEventListener("orientationchange", nachsehen);
  // Wer das Feld verlässt, bekommt die Lage, die inzwischen gilt.
  document.addEventListener("focusout", () => window.setTimeout(nachsehen, 0));
}

/** Wer erfahren will, dass aus einem Schreibtisch ein Telefon wurde. */
export function aufGeraetwechsel(h: () => void): () => void {
  einhaengen();
  horcher.add(h);
  return () => { horcher.delete(h); };
}

function abonnieren(h: () => void): () => void {
  return aufGeraetwechsel(h);
}

/* ══════════════ Die Lage an der Wurzel — für die Stile ══════════════

   Luca, 17.09.2026: *„Wir wollen die Mobile-App ja nicht an die Adresse
   binden, sondern daran, dass wir von dem Mobile aus arbeiten."*

   Bis hierher hatten Stil und Code **zwei Antworten** auf dieselbe Frage:
   Diese Datei sagte „Telefon" bei 844 × 390 mit Finger, die Stile sagten
   es bei `max-width: 640px` (`handy.css`) oder `720px` (`schale.css`). Ein
   gedrehtes Telefon bekam damit die Telefonmaske mit Schreibtischmaßen.

   **Jetzt steht die Antwort als Attribut an `<html>`:**
   `data-geraet="telefon" | "tablet" | "schreibtisch"`. Ein Stil, der die
   Telefonfassung meint, fragt `:root[data-geraet="telefon"]` und liest
   damit dieselbe Lage wie `useTelefon()`. Die Haltepunkte in den Stilen
   bleiben, wo sie eine **Breite** meinen — ein schmal gezogenes Fenster am
   Schreibtisch ist ohnehin auch hier ein Telefon.

   Gesetzt wird es **vor dem ersten Bild** (`main.tsx`), sonst zeichnet
   jede Maske einmal in der Schreibtischfassung und springt danach.

   **Ein Skript in `index.html` braucht es dafür nicht** (geprüft am
   19.09.2026). Vor React steht auf dem Schirm nichts als der leere
   `<body>`, und keine Regel, die `data-geraet` fragt, gilt ihm — sie
   gelten alle Bausteinen, die erst React zeichnet. `main.tsx` setzt das
   Attribut, bevor React das erste Mal zeichnet; nachgemessen: Beim
   ersten Kind in `#wurzel` steht es schon, am Telefon wie am
   Schreibtisch. Ein zweites Skript wäre eine zweite Antwort auf dieselbe
   Frage, und die liefe irgendwann auseinander (40-mobiles-grundgeruest.md,
   „Eine Quelle der Wahrheit", Punkt 3). Anders als bei `data-theme`: Die
   Farbe des leeren `<body>` hängt am Thema, nicht an der Lage. */
export function geraetAnWurzel(): () => void {
  if (typeof document === "undefined") return () => {};
  const setzen = () => { document.documentElement.dataset["geraet"] = geraetJetzt(); };
  setzen();
  return aufGeraetwechsel(setzen);
}

/* Der Stand für den Server: Dort gibt es kein Fenster, und die Fassung
   für den Schreibtisch ist die, der nichts fehlt. */
const AM_SERVER = (): Geraet => "schreibtisch";

/**
 * Die Lage als Haken — er zeichnet neu, wenn sie sich ändert.
 *
 * `useSyncExternalStore` und kein `useEffect` mit `useState`: Die Antwort
 * steht schon beim **ersten** Bild richtig da. Ein Nachreichen im Effekt
 * hieße, dass jede Maske einmal in der falschen Fassung erscheint und
 * danach springt.
 */
export function useGeraet(): Geraet {
  return useSyncExternalStore(abonnieren, geraetJetzt, AM_SERVER);
}

/** Gilt hier die Telefonfassung? Die Kurzform für die Masken. */
export function useTelefon(): boolean {
  return useGeraet() === "telefon";
}
